import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Fuel, AlertCircle, Copy, RotateCcw, Info, Leaf } from 'lucide-react';
import {
  DEFAULT_INPUTS,
  MODES,
  FUELS,
  GRID_PRESETS,
  PRECISION_OPTIONS,
  DEFAULT_PRECISION,
  unitsFor,
  validateValue,
  validateGridIntensity,
} from '../../lib/fuelConsumptionTools';
import { fuelConsumptionToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const CUSTOM_PRESET = 'custom';

// CO2 is shown to 0.1 g/km, rounded half-up with an epsilon like the backend's round_():
// plain toFixed(1) turns 0.15 x 727 = 109.05 (stored as 109.04999...) into 109.0.
const formatCo2 = (n) => (Math.sign(n) * Math.floor(Math.abs(n) * 10 + 0.5 + 1e-9) / 10).toFixed(1);

const FieldError = ({ id, error }) => (
  <p id={id} className="flex items-center gap-1 text-xs text-destructive" data-testid={id} role="alert">
    <AlertCircle className="h-3 w-3 shrink-0" /> {error}
  </p>
);

const FuelConsumptionConverter = () => {
  const [mode, setMode] = useState(DEFAULT_INPUTS.mode);
  const [fuel, setFuel] = useState(DEFAULT_INPUTS.fuel);
  const [fuelUnit, setFuelUnit] = useState(DEFAULT_INPUTS.fuelUnit);
  const [fuelValue, setFuelValue] = useState(String(DEFAULT_INPUTS.fuelValue.toFixed(1)));
  const [electricUnit, setElectricUnit] = useState(DEFAULT_INPUTS.electricUnit);
  const [electricValue, setElectricValue] = useState(String(DEFAULT_INPUTS.electricValue));
  const [gridPreset, setGridPreset] = useState(DEFAULT_INPUTS.gridPreset);
  const [gridIntensity, setGridIntensity] = useState(String(DEFAULT_INPUTS.gridIntensity));
  const [precision, setPrecision] = useState(DEFAULT_PRECISION);

  const isElectric = mode === 'electric';
  const units = unitsFor(mode, fuel);
  const unit = isElectric ? electricUnit : fuelUnit;
  const rawValue = isElectric ? electricValue : fuelValue;
  const unitInfo = units.find((u) => u.value === unit) || units[0];

  const valueError = validateValue(rawValue, units, unit);
  const gridError = isElectric ? validateGridIntensity(gridIntensity) : null;
  const fieldError = valueError || gridError;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (fieldError) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const body = isElectric
      ? { mode, value: Number(rawValue), unit, gridIntensity: gridIntensity === '' ? '' : Number(gridIntensity) }
      : { mode, value: Number(rawValue), unit, fuel };
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      fuelConsumptionToolApi.convert(body)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, rawValue, unit, fuel, gridIntensity, isElectric, fieldError]);

  // Only render a response that matches the current mode/unit/fuel — a stale one from
  // before a fuel or mode switch has a different set of unit keys.
  const current = results && results.mode === mode && results.unit === unit
    && (isElectric || results.fuel === fuel) ? results : null;
  const isMassBasis = !isElectric && (FUELS.find((f) => f.value === fuel) || FUELS[0]).basis === 'kg';

  const formatted = useMemo(() => {
    if (!current) return null;
    return units.map((u) => ({ ...u, display: current.values[u.value].toFixed(precision) }));
  }, [current, units, precision]);

  const handleFuelChange = (next) => {
    setFuel(next);
    const nextUnits = unitsFor('fuel', next);
    if (!nextUnits.some((u) => u.value === fuelUnit)) setFuelUnit(nextUnits[0].value);
  };

  const handlePresetChange = (next) => {
    setGridPreset(next);
    const preset = GRID_PRESETS.find((p) => p.value === next);
    if (preset) setGridIntensity(String(preset.gPerKwh));
  };

  const handleGridIntensityChange = (raw) => {
    setGridIntensity(raw);
    const match = GRID_PRESETS.find((p) => String(p.gPerKwh) === raw.trim());
    setGridPreset(match ? match.value : CUSTOM_PRESET);
  };

  const handleReset = () => {
    setFuel(DEFAULT_INPUTS.fuel);
    setFuelUnit(DEFAULT_INPUTS.fuelUnit);
    setFuelValue(DEFAULT_INPUTS.fuelValue.toFixed(1));
    setElectricUnit(DEFAULT_INPUTS.electricUnit);
    setElectricValue(String(DEFAULT_INPUTS.electricValue));
    setGridPreset(DEFAULT_INPUTS.gridPreset);
    setGridIntensity(String(DEFAULT_INPUTS.gridIntensity));
    setPrecision(DEFAULT_PRECISION);
  };

  const handleCopy = async () => {
    if (!formatted) return;
    const lines = formatted.map((u) => `${u.name}: ${u.display} ${u.label}`);
    if (isElectric) {
      lines.push(`MPGe: ${current.mpge.toFixed(precision)}`);
      if (current.co2) lines.push(`CO2: ${formatCo2(current.co2.gPerKm)} g/km at ${current.co2.gridIntensity} g CO2/kWh`);
    } else {
      lines.push(`CO2 (${current.fuelLabel}): ${formatCo2(current.co2.gPerKm)} g/km`);
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const selectedPreset = GRID_PRESETS.find((p) => p.value === gridPreset);

  return (
    <div className="space-y-6" aria-label="Fuel Consumption and CO2 Converter">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Fuel className="h-5 w-5 text-autoxium-blue" />
            Fuel Consumption &amp; CO₂ Converter
          </CardTitle>
          <CardDescription>
            Convert fuel or electric efficiency between units and estimate tank-to-wheel CO₂ per
            km. Results update live as you type.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="flex-wrap h-auto" data-testid="fuel-mode-tabs">
              {MODES.map((m) => (
                <TabsTrigger key={m.id} value={m.id} data-testid={`fuel-mode-${m.id}`}>{m.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="technical-card lg:col-span-2 h-fit">
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {isElectric ? 'Energy Consumption' : 'Fuel Economy'}
              </h3>
              <div className="space-y-4">
                {!isElectric && (
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Fuel</Label>
                    <Select value={fuel} onValueChange={handleFuelChange}>
                      <SelectTrigger data-testid="fuel-fuel-select" aria-label="Fuel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FUELS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="fuel-value" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Value <span className="normal-case text-muted-foreground/70">({unitInfo.label})</span>
                  </Label>
                  <Input
                    id="fuel-value"
                    type="number"
                    step="any"
                    min="0"
                    value={rawValue}
                    onChange={(e) => (isElectric ? setElectricValue : setFuelValue)(e.target.value)}
                    className="font-mono"
                    aria-invalid={!!valueError}
                    aria-describedby={valueError ? 'fuel-value-error' : undefined}
                    data-testid="fuel-value-input"
                  />
                  {valueError && <FieldError id="fuel-value-error" error={valueError} />}
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Input Unit</Label>
                  <Select value={unit} onValueChange={isElectric ? setElectricUnit : setFuelUnit}>
                    <SelectTrigger data-testid="fuel-unit-select" aria-label="Input unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((u) => (
                        <SelectItem key={u.value} value={u.value}>{u.name} ({u.label})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {isElectric && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-md">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Grid CO₂ (optional)
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Preset</Label>
                    <Select value={gridPreset} onValueChange={handlePresetChange}>
                      <SelectTrigger data-testid="fuel-grid-preset-select" aria-label="Grid intensity preset">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GRID_PRESETS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label} ({p.gPerKwh} g/kWh)</SelectItem>
                        ))}
                        <SelectItem value={CUSTOM_PRESET}>Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fuel-grid-intensity" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Grid intensity <span className="normal-case text-muted-foreground/70">(g CO₂/kWh)</span>
                    </Label>
                    <Input
                      id="fuel-grid-intensity"
                      type="number"
                      step="any"
                      min="0"
                      value={gridIntensity}
                      onChange={(e) => handleGridIntensityChange(e.target.value)}
                      className="font-mono"
                      aria-invalid={!!gridError}
                      aria-describedby={gridError ? 'fuel-grid-error' : undefined}
                      data-testid="fuel-grid-input"
                    />
                    {gridError && <FieldError id="fuel-grid-error" error={gridError} />}
                    <p className="text-[11px] text-muted-foreground">
                      {selectedPreset ? `Source: ${selectedPreset.source}.` : 'Leave blank to skip the CO₂ estimate.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Display</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Precision</Label>
                  <Select value={String(precision)} onValueChange={(p) => setPrecision(Number(p))}>
                    <SelectTrigger data-testid="fuel-precision-select" aria-label="Display precision">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRECISION_OPTIONS.map((p) => (
                        <SelectItem key={p} value={String(p)}>{p} decimal places</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={handleCopy} disabled={!formatted} data-testid="fuel-copy-btn">
                    <Copy className="h-4 w-4" /> Copy
                  </Button>
                  <Button type="button" variant="outline" className="flex-1" onClick={handleReset} data-testid="fuel-reset-btn">
                    <RotateCcw className="h-4 w-4" /> Reset
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-3 space-y-4 min-w-0">
          <Card className="technical-card h-fit">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Converted Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div role="status" aria-live="polite">
                {fieldError ? (
                  <p className="text-sm text-muted-foreground" data-testid="fuel-results-placeholder">
                    Fix the highlighted field to see results.
                  </p>
                ) : calcError ? (
                  <div className="flex items-center gap-2 text-sm text-destructive" data-testid="fuel-calc-error">
                    <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
                  </div>
                ) : !formatted ? (
                  <p className="text-sm text-muted-foreground" data-testid="fuel-results-loading">Calculating…</p>
                ) : (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="fuel-results">
                      {formatted.map((u) => (
                        <div
                          key={u.value}
                          className={`p-3 rounded-md border ${u.value === unit ? 'border-autoxium-blue bg-autoxium-blue/5' : 'border-structure bg-muted/30'}`}
                          data-testid={`fuel-result-${u.value}`}
                        >
                          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                            {u.label} <span className="normal-case">— {u.name}</span>
                          </p>
                          <p className={`font-mono font-bold break-all ${u.value === unit ? 'text-lg text-autoxium-blue' : 'text-base'}`}>
                            {u.display}
                          </p>
                        </div>
                      ))}
                      {isElectric && (
                        <div className="p-3 rounded-md border border-structure bg-muted/30" data-testid="fuel-result-mpge">
                          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                            MPGe <span className="normal-case">— Miles per gallon equivalent</span>
                          </p>
                          <p className="font-mono font-bold text-base break-all">{current.mpge.toFixed(precision)}</p>
                        </div>
                      )}
                    </div>

                    {current.co2 ? (
                      <div className="p-3 rounded-md border border-emerald-500/40 bg-emerald-500/5" data-testid="fuel-result-co2">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                          <Leaf className="h-3 w-3" /> CO₂ — tank-to-wheel
                        </p>
                        <p className="font-mono font-bold text-lg break-all">{formatCo2(current.co2.gPerKm)} g CO₂/km</p>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {isElectric
                            ? `At ${current.co2.gridIntensity} g CO₂/kWh grid intensity.`
                            : `${current.fuelLabel}: ${current.co2.factor.toFixed(4)} ${current.co2.factorUnit} · ${current.co2.source}.`}
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="fuel-co2-skipped">
                        Enter a grid intensity to estimate CO₂ per km.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="technical-card" data-testid="fuel-formula">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-sm">Formula &amp; Derivation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
              <div>
                <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">method</Badge>
                These conversions are <strong>reciprocal</strong>, not multiplicative: every value is
                reduced to {units[0].label}, then expanded back out. Zero has no finite equivalent
                (0 {units[0].label} would be infinite {units[1].label}), so values must be greater than 0.
              </div>
              {isElectric ? (
                <>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">from kWh/100 km = x</Badge>
                    Wh/km = 10x · km/kWh = 100 ÷ x · mi/kWh = 100 ÷ (x × 1.609344)  · 
                    MPGe = 33.705 × 100 ÷ (x × 1.609344)
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">CO₂</Badge>
                    g CO₂/km = (kWh/100 km ÷ 100) × grid intensity (g CO₂/kWh)
                  </div>
                  <div className="flex gap-1.5 items-start" data-testid="fuel-mpge-note">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-autoxium-blue" />
                    <span>
                      MPGe uses the EPA definition, 33.705 kWh per US gallon of gasoline equivalent, and
                      assumes the value entered is <strong>wall-plug</strong> energy, including charging
                      losses. A dashboard (battery-out) figure will overstate MPGe.
                    </span>
                  </div>
                </>
              ) : isMassBasis ? (
                <>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">from kg/100 km = x</Badge>
                    km/kg = 100 ÷ x
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">CO₂</Badge>
                    g CO₂/km = (kg/100 km) × (kg CO₂/kg) × 10
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">from L/100 km = x</Badge>
                    km/L = 100 ÷ x · mpg (US) = 235.214583 ÷ x · mpg (UK) = 282.480936 ÷ x
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">constants</Badge>
                    1 US gal = 3.785411784 L · 1 imp gal = 4.54609 L · 1 mi = 1.609344 km
                  </div>
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">CO₂</Badge>
                    g CO₂/km = (L/100 km) × (kg CO₂/L) × 10
                  </div>
                </>
              )}
              {current && current.co2 && !isElectric && (
                <div className="pt-1 font-mono text-[11px] bg-muted/50 rounded-md px-3 py-2 break-all">
                  {current.values[units[0].value].toFixed(Math.max(precision, 3))} × {current.co2.factor.toFixed(4)} × 10
                  {' '}= {formatCo2(current.co2.gPerKm)} g CO₂/km
                </div>
              )}
            </CardContent>
          </Card>

          {!isElectric && current && (
            <Card className="technical-card" data-testid="fuel-factor-table">
              <CardHeader>
                <CardTitle className="font-chivo uppercase tracking-tight text-sm">CO₂ Factors Used</CardTitle>
                <CardDescription className="text-xs">
                  Tank-to-wheel, all carbon at the tailpipe (ethanol and biodiesel carbon included). Derived
                  from {current.co2.source}: density × carbon mass fraction × 44.009/12.011.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fuel</TableHead>
                      <TableHead className="text-right">Factor</TableHead>
                      <TableHead className="hidden md:table-cell">Derivation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {current.fuelFactors.map((f) => (
                      <TableRow key={f.value} className={f.value === current.fuel ? 'bg-autoxium-blue/5 font-semibold' : ''}>
                        <TableCell className="text-xs">{f.label}</TableCell>
                        <TableCell className="text-right font-mono text-xs whitespace-nowrap">{f.factor.toFixed(4)} {f.factorUnit}</TableCell>
                        <TableCell className="hidden md:table-cell text-[11px] text-muted-foreground">{f.derivation}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {isElectric && (
            <Card className="technical-card" data-testid="fuel-grid-table">
              <CardHeader>
                <CardTitle className="font-chivo uppercase tracking-tight text-sm">Grid Intensity Presets</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Region</TableHead>
                      <TableHead className="text-right">g CO₂/kWh</TableHead>
                      <TableHead className="hidden md:table-cell">Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {GRID_PRESETS.map((p) => (
                      <TableRow key={p.value} className={p.value === gridPreset ? 'bg-autoxium-blue/5 font-semibold' : ''}>
                        <TableCell className="text-xs">{p.label}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{p.gPerKwh}</TableCell>
                        <TableCell className="hidden md:table-cell text-[11px] text-muted-foreground">{p.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuelConsumptionConverter;
